import {
  ArrowDropDown,
  ArrowRight,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox,
} from '@material-ui/icons';
import { assocPath, clone, path } from 'ramda';
import React from 'react';

import { TreeItem, TreeItemRaw, TreeItems } from './Tree.types';

export const CheckboxTreeIcons = {
  check: <CheckBox />,
  uncheck: <CheckBoxOutlineBlank />,
  halfCheck: <IndeterminateCheckBox />,
  expandClose: <ArrowRight />,
  expandOpen: <ArrowDropDown />,
};

export function getCheckedItems(items: TreeItems, checked: string[] = []) {
  return items.reduce((arr: string[], item: TreeItem) => {
    if (item.checked) {
      arr.push(item.id);
    }

    if (Array.isArray(item.values) && item.values.length) {
      arr = getCheckedItems(item.values, arr);
    }

    return arr;
  }, checked);
}

export function filterItems(items: TreeItems, filterText: string) {
  return items.map((item: TreeItem) => {
    let isAllChildrenHidden = false;

    if (item.isParent) {
      const children = filterItems(item.values || [], filterText);
      isAllChildrenHidden = !children.some((child: TreeItem) => !child.hidden);
    }

    item.hidden = !(
      item.name.toLocaleLowerCase().indexOf(filterText.toLocaleLowerCase()) > -1 ||
      (item.isParent && !isAllChildrenHidden)
    );

    return item;
  });
}

export const hasSearchResult = (items: TreeItems) => items.some((item: TreeItem) => !item.hidden);

export function parseDepth(depth: number[], propName = 'checked') {
  return depth.reduce((arr: (number | string)[], item: number, index: number) => {
    arr.push(item);

    if (index !== depth.length - 1) {
      arr.push('values');
    } else if (propName) {
      arr.push(propName);
    }

    return arr;
  }, []);
}

export function getItem(depth: number[], items: TreeItems): TreeItem {
  return path(parseDepth(depth, ''), items) as TreeItem;
}

export function toggleChecked(
  items: TreeItems,
  item: TreeItem,
  isChecked: boolean,
  percolateUpward = true,
) {
  if (item.isLeaf) {
    items = assocPath(parseDepth(item.depth), isChecked, items);
  } else if (Array.isArray(item.values) && item.values.length) {
    item.values.forEach((child: TreeItem) => {
      items = toggleChecked(items, child, isChecked, false);
    });
  }

  items = assocPath(parseDepth(item.depth, 'checkState'), isChecked ? 1 : 0, items);

  if (percolateUpward && item.isChild && item.parentDepth) {
    items = toggleParentStatus(items, getItem(item.parentDepth, items));
  }

  return items;
}

export function toggleParentStatus(items: TreeItems, item: TreeItem) {
  const checkState = determineShallowCheckState(item);

  items = assocPath(parseDepth(item.depth, 'checkState'), checkState, items);

  if (item.isChild && item.parentDepth) {
    items = toggleParentStatus(items, getItem(item.parentDepth, items));
  }

  return items;
}

export function determineShallowCheckState(item: TreeItem) {
  if (item.isLeaf) {
    return item.checked ? 1 : 0;
  }

  if (isEveryChildChecked(item)) {
    return 1;
  }

  if (isSomeChildChecked(item)) {
    return 2;
  }

  return 0;
}

export function isEveryChildChecked(item: TreeItem) {
  return (item.values || []).every((child: TreeItem) => child.checkState === 1);
}

export function isSomeChildChecked(item: TreeItem) {
  return (item.values || []).some((child: TreeItem) => child.checkState > 0);
}

export function prepareModel(items: TreeItemRaw[], parent: any = {}) {
  return items.map((item: any, index: number) => {
    const depth = parent.depth ? clone(parent.depth) : [];
    depth.push(index);
    item.depth = depth;
    item.checked = false;
    item.hidden = false;
    item.checkState = 0;
    item.parentDepth = parent.depth ? clone(parent.depth) : null;
    item.isParent = Array.isArray(item.values) && item.values.length;
    item.isLeaf = !item.isParent;
    item.isChild = parent.id !== undefined;

    if (Array.isArray(item.values) && item.values.length) {
      item.values = prepareModel(item.values, item);
    }

    return item;
  });
}

export function initializeChecked(
  items: TreeItems,
  iterableItems: TreeItems,
  checkedItems: string[],
) {
  iterableItems.forEach((item: TreeItem) => {
    if (checkedItems.indexOf(item.id) !== -1) {
      items = toggleChecked(items, item, true);
    }

    if (Array.isArray(item.values) && item.values.length) {
      items = initializeChecked(items, item.values, checkedItems);
    }
  });

  return items;
}

export function getCheckState(item: TreeItem) {
  if (item.checkState === 0 || item.checkState === 2) {
    return true;
  }

  return false;
}

export function renderCheckboxIcon(item: TreeItem) {
  if (item.checkState === 0) {
    return CheckboxTreeIcons.uncheck;
  }

  if (item.checkState === 1) {
    return CheckboxTreeIcons.check;
  }

  return CheckboxTreeIcons.halfCheck;
}

export function renderCollapseIcon(isExpanded: boolean) {
  if (!isExpanded) {
    return CheckboxTreeIcons.expandClose;
  }

  return CheckboxTreeIcons.expandOpen;
}
